import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import styled from 'styled-components';
import {
  faArrowLeft,
  faCommentSlash,
  faEnvelope,
  faFileLines,
  faHouse,
  faInbox,
  faPen,
  faPhone,
  faToolbox
} from '@fortawesome/free-solid-svg-icons';
import { CallDrawer } from '../../components';
import { useAuth } from '../../context/AuthContext';
import { formatPhone } from '../../utils/callLog';
import { useClientQuery } from '../../queries/clients';
import { useOwnerQuery, useUploadOwnerFiles } from '../../queries/owners';
import { useVendorQuery } from '../../queries/vendors';
import { useProspects } from '../../queries/prospects';
import { useActions } from '../../queries/actions';
import { usePhone } from '../../hooks/usePhone';
import { onPhone } from '../../theme/tokens';
import { portalApi } from '../../services/portalApi';
import { downloadBlob } from '../../utils/csv';
import { errorText } from '../../utils/errors';
import { clientType, last10 } from '../../utils/contacts';
import { AaTheme, Av, Btn, I, Seg, cx } from '../Todos/ui';
import TenantFormModal from '../Tenants/TenantFormModal';
import ProspectModal from '../Prospects/ProspectModal';
import OwnerModal from '../Owners/OwnerModal';
import VendorFormModal from '../Vendors/VendorFormModal';
import { FileDrop } from '../Owners/OwnerFormModal';
import Quick, { QuickText } from './Quick';
import Overview from './Overview';
import Activity from './Activity';
import Chat from './Chat';
import { Lrow, TypeChip } from './parts';
import { scheduleInitial, useSchedule } from './useSchedule';
import '../Todos/aa.css';
import '../../styles/mcc-pages.css';

const Hbtns = styled.div.attrs({ className: 'cp-hbtns' })`
  ${onPhone} {
    &&& {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    .qa7 {
      display: contents;
    }
    button {
      width: 100%;
      justify-content: center;
    }
  }
`;

const EDIT_PERM = { tenant: 'manage_clients', prospect: 'manage_prospects', applicant: 'manage_prospects', owner: 'manage_owners', vendor: 'manage_vendors' };

function toContact(kind, id, data) {
  if (!data) return null;
  if (kind === 'client') {
    const x = data.client;
    return { id, kind, type: clientType(x), name: x.name, phone: x.phone, email: x.email, home: x.unit?.name || x.interestedProperty || '' };
  }
  if (kind === 'owner') {
    const x = data.owner;
    const props = x.properties || [];
    const home = props.length ? `${props[0].name}${props.length > 1 ? ` +${props.length - 1} more` : ''}` : '';
    return { id, kind, type: 'owner', name: x.name, phone: x.phone, email: x.email, home };
  }
  const x = data.vendor;
  return { id, kind, type: 'vendor', name: x.company, phone: x.phone, email: x.email, home: x.trade || '' };
}

function Documents({ owner }) {
  const upload = useUploadOwnerFiles();
  const files = owner.files || [];
  const view = (f) =>
    portalApi
      .ownerFile(owner._id, f._id)
      .then((blob) => downloadBlob(f.name, blob))
      .catch((err) => toast.error(errorText(err, 'Could not open this file')));
  return (
    <div className="m-col gap12">
      <div className="m-row">
        <div className="grow sm">{files.length} documents</div>
      </div>
      <FileDrop onAdd={(added) => upload.mutate({ id: owner._id, files: added })} />
      {files.length > 0 && (
        <div className="cp-feed">
          {files.map((f) => (
            <Lrow
              key={f._id}
              icon={faFileLines}
              title={f.name}
              sub={`${f.type} · ${dayjs(f.addedAt).format('MMM D, YYYY')}`}
              right={
                <button type="button" className="aa-link sm" onClick={() => view(f)}>
                  View
                </button>
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

function useContactData(kind, id) {
  const client = useClientQuery(kind === 'client' ? id : null);
  const owner = useOwnerQuery(kind === 'owner' ? id : null);
  const vendor = useVendorQuery(kind === 'vendor' ? id : null);
  return { client, owner, vendor }[kind] || {};
}

export default function Profile() {
  const { kind, id } = useParams();
  const navigate = useNavigate();
  const phone = usePhone();
  const { can } = useAuth();
  const { data, isLoading } = useContactData(kind, id);
  const { rows: tasks } = useActions('d');
  const { rows: prospects } = useProspects();
  const [tab, setTab] = useState('overview');
  const [texting, setTexting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [callId, setCallId] = useState(null);
  const [schedule, scheduleNode] = useSchedule();
  const c = toContact(kind, id, data);

  if (!c)
    return (
      <AaTheme>
        <div className="aa">
          <div className="m-page">
            <div className="m-emptyc">
              <div className="b">{isLoading ? 'Loading…' : 'Contact not found.'}</div>
              {!isLoading && <Btn onClick={() => navigate('/contacts')}>Back to Contacts</Btn>}
            </div>
          </div>
        </div>
      </AaTheme>
    );

  const mine = tasks.filter((x) => c.phone && last10(x.contact?.phone) === last10(c.phone));
  const open = mine.filter((x) => x.lane !== 'done');
  const done = mine.filter((x) => x.lane === 'done');
  const onSchedule = (k) => schedule(k, scheduleInitial(k, c));
  const tabs = [['overview', 'Overview'], ['activity', 'Activity'], ...(phone && c.phone ? [['messages', 'Messages']] : []), ...(kind === 'owner' ? [['documents', 'Documents', null, (data.owner.files || []).length]] : [])];
  const prospect = c.type !== 'tenant' && kind === 'client' ? prospects.find((p) => p._id === id) : null;

  let edit = null;
  if (editing) {
    const close = () => setEditing(false);
    if (c.type === 'tenant') edit = <TenantFormModal tenant={data.client} onClose={close} />;
    else if (kind === 'client' && prospect) edit = <ProspectModal prospect={prospect} rows={prospects} canManage onClose={close} />;
    else if (kind === 'owner') edit = <OwnerModal id={id} preview={data.owner} canManage onClose={close} />;
    else if (kind === 'vendor') edit = <VendorFormModal vendor={data.vendor} onClose={close} />;
  }

  return (
    <AaTheme>
      <div className={cx('cp-shell', !phone && c.phone && 'with-chat')}>
        <div className={cx('aa', phone && 'ph')}>
          <div className="m-page">
          <div className="cp" data-type={c.type}>
            <div className="cp-head">
              <Btn sm icon={faArrowLeft} className="text cp-back" onClick={() => navigate('/contacts')}>
                Contacts
              </Btn>
              <div className="cp-hrow">
                <Av name={c.name} />
                <div className="cp-hmain">
                  <div className="m-row wrap gap4">
                    <h1>{c.name}</h1>
                    <TypeChip type={c.type} />
                  </div>
                  <div className="cp-hsub">
                    <span>
                      <I icon={c.phone ? faPhone : faCommentSlash} /> {c.phone ? formatPhone(c.phone) : 'No phone'}
                    </span>
                    {c.email && (
                      <span>
                        <I icon={faEnvelope} /> {c.email}
                      </span>
                    )}
                    {c.home && (
                      <span>
                        <I icon={c.type === 'vendor' ? faToolbox : faHouse} /> {c.home}
                      </span>
                    )}
                  </div>
                </div>
                <Hbtns>
                  <Quick contact={c} primaryCall onText={() => setTexting(!texting)} onAction={onSchedule} />
                  <Btn sm icon={faInbox} disabled={!c.phone} onClick={() => navigate('/inbox', { state: { contactNumber: c.phone } })}>
                    Open in Inbox
                  </Btn>
                  {can(EDIT_PERM[c.type]) && (
                    <Btn sm icon={faPen} onClick={() => setEditing(true)}>
                      Edit
                    </Btn>
                  )}
                </Hbtns>
              </div>
              {texting && <QuickText contact={c} onClose={() => setTexting(false)} />}
            </div>
            <div className="aa-types">
              <Seg items={tabs} value={tab} onChange={setTab} label="Profile section" />
            </div>
            {tab === 'messages' ? null : tab === 'activity' ? (
              <Activity c={c} data={data} done={done} onCall={setCallId} />
            ) : tab === 'documents' && kind === 'owner' ? (
              <Documents owner={data.owner} />
            ) : (
              <Overview c={c} data={data} actions={open} phone={phone} onSchedule={onSchedule} onDocs={() => setTab('documents')} />
            )}
          </div>
          </div>
        </div>
        {c.phone && (!phone || tab === 'messages') && <Chat contact={c} onProfile={() => setTab('overview')} />}
      </div>
      {edit}
      {scheduleNode}
      <CallDrawer callId={callId} onClose={() => setCallId(null)} />
    </AaTheme>
  );
}
